import { Injectable } from '@angular/core';

export interface CapturedRequest {
  timestamp: string;
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody: any;
  responseStatus: number;
  responseHeaders: Record<string, string>;
  responseBody: any;
  duration: number;
  isError?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CaptureStorage {
  private dbName = 'msw-capture-db';
  private storeName = 'captured-requests';
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  async saveCapture(capture: CapturedRequest): Promise<void> {
    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      const store = this.getObjectStore('readwrite');
      const request = store.add(capture);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAllCaptures(): Promise<CapturedRequest[]> {
    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      const store = this.getObjectStore('readonly');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearCaptures(): Promise<void> {
    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      const store = this.getObjectStore('readwrite');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async ensureDBInitialized(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
  }

  private getObjectStore(mode: IDBTransactionMode): IDBObjectStore {
    const transaction = this.db!.transaction([this.storeName], mode);
    return transaction.objectStore(this.storeName);
  }
}
